import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { DownOutlined, Drawer, Radio } from '@fx-ui/fine-design';
import { SearchInput } from '@fx-ui/jdy-design-biz';
import { filter, isEmpty, isSearchedByKeyword, map } from '@libs/shared';
import HighLightText from '../../../highlight-text';
import { getAreaCodeList } from '../../../../utils';
import { platformIntl } from '../../../../constants';

interface IMobileAreaCodeSelectorProps {
    areaCode: string;
    disabled: boolean;
    height?: string | number;
    onAreaCodeChange: (areaCodeVal: string) => void;
}

const MobileAreaCodeSelector: React.FC<IMobileAreaCodeSelectorProps> = (props) => {
    const { areaCode, disabled, height, onAreaCodeChange } = props;
    const DEFAULT_AREA_CODE_LIST = useMemo(() => getAreaCodeList(), []);

    const [areaCodeDrawerVisible, setAreaCodeDrawerVisible] = useState<boolean>(false);
    const [keyword, setKeyword] = useState<string>('');

    /**
     * 根据搜索关键词过滤出区号列表
     *
     * @param keyword 关键词
     */
    const areaCodeList = useMemo(() => {
        if (!keyword) {
            return DEFAULT_AREA_CODE_LIST;
        }
        return map(DEFAULT_AREA_CODE_LIST, (areaCodeGroup) => {
            const { label, children: areaCodeItems } = areaCodeGroup;
            return {
                label,
                children: filter(areaCodeItems, (item) => {
                    return isSearchedByKeyword(item.text, keyword);
                })
            };
        });
    }, [keyword]);

    useEffect(() => {
        // 关闭区号选择Drawer时，重置搜索关键词
        if (!areaCodeDrawerVisible) {
            setKeyword('');
        }
    }, [areaCodeDrawerVisible]);
    return (
        <>
            <div
                className={classNames('fx-mobile-area-code-selector', 'fx-area-code', {
                    disabled: disabled
                })}
                style={{ height: height }}
                onClick={() => {
                    if (disabled) {
                        return;
                    }
                    setAreaCodeDrawerVisible(true);
                }}
            >
                <span>{areaCode}</span>
                <DownOutlined className="select-icon" />
            </div>
            <Drawer
                className="fx-mobile-area-code-drawer"
                visible={areaCodeDrawerVisible}
                autoClose
                closable={false}
                placement="bottom"
                edge={{ height: 520 }}
                onClose={() => {
                    setAreaCodeDrawerVisible(false);
                }}
            >
                <div className="area-code-search">
                    <SearchInput
                        placeholder={platformIntl.get('phone.area.code.placeholder')}
                        autoFocus
                        allowClear
                        debounceTime={500}
                        onSearchStart={(keyword) => {
                            setKeyword(keyword);
                        }}
                        onClearBtnClick={() => {
                            setKeyword('');
                        }}
                    />
                </div>
                <div className="area-code-list">
                    {map(areaCodeList, (areaCodeGroup) => {
                        const { label, children: areaCodeItems } = areaCodeGroup;
                        return (
                            <div className="area-code-group" key={label}>
                                {label && !isEmpty(areaCodeItems) && (
                                    <div className="area-code-label">{label}</div>
                                )}
                                {map(areaCodeItems, (item) => {
                                    return (
                                        <div className="area-code-item" key={item.text}>
                                            <Radio
                                                value={item.value}
                                                checked={item.value === areaCode}
                                                onChange={(value) => {
                                                    onAreaCodeChange(value);
                                                    setAreaCodeDrawerVisible(false);
                                                }}
                                            >
                                                <HighLightText
                                                    text={item.text}
                                                    keyword={keyword}
                                                    abbreviated={false}
                                                />
                                            </Radio>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </Drawer>
        </>
    );
};

export default MobileAreaCodeSelector;
